import { ReviewMode } from "@app/secured/GradebookDayPageV2/GradebookDayPageV2";
import Container from "@app/secured/LearningPage/components/Container";
import {
  ActivityStatus,
  Card,
  cn,
  Icon,
  LinearProgress,
  useMediaMobileScreen,
} from "@cca/components";
import { LearningModuleItemDto } from "@core/types";
import { ActivityDto } from "@core/typesV2";
import { dateFormatter, formattedOrDefault } from "@core/utils";
import { type PropsWithChildren, type RefCallback } from "react";
import { NavLink, useParams } from "react-router-dom";

import { Loader } from "../Loader";
import ReviewBadge from "../ReviewBadge";
import { DayBreadcrumbs, DayItemIcon } from "./components";

type RenderNavLinkParam = {
  activity: ActivityDto;
  isReview: boolean;
  reviewMode?: ReviewMode;
  isMobile: boolean;
  isActive: boolean;
};

const renderContentVersion = (activity: ActivityDto) =>
  activity.contentVersion && activity.contentVersion > 1 ? (
    <span className="shrink-0 rounded bg-gray-100 px-1.5 py-0.5 text-xs font-semibold text-gray-600">
      v{activity.contentVersion}
    </span>
  ) : null;

const renderNavLink = ({
  activity,
  isReview,
  reviewMode,
  isMobile,
  isActive,
}: RenderNavLinkParam) => {
  if (isMobile) {
    return (
      <>
        <DayItemIcon
          activity={activity}
          isActive={isActive}
          isReview={isReview}
        />
        <span className="line-clamp-2 min-w-0 flex-1">
          {activity.title || activity.activityId}
        </span>
        {renderContentVersion(activity)}
        {isMobile && (
          <Icon
            className="icon ml-auto text-base text-gray-400"
            type="chevron-right"
          />
        )}
      </>
    );
  }

  const renderGradebookLink = () => (
    <div className="flex w-full gap-3">
      <div className="flex pt-2">
        <DayItemIcon activity={activity} isReview={isReview} />
      </div>
      <div className="flex flex-1 flex-col gap-2">
        <div className="flex min-w-0 items-start gap-2">
          <span className="line-clamp-2 min-w-0 flex-1">
            {activity.title || activity.activityId}
          </span>
          {renderContentVersion(activity)}
        </div>
        {activity.progress && (
          <>
            <ReviewBadge
              status={activity.progress?.status || ActivityStatus.PendingGrade}
            />
          </>
        )}
      </div>
    </div>
  );

  const renderUngradedLink = () => (
    <div className="flex w-full gap-3">
      <div className="flex pt-2">
        <DayItemIcon activity={activity} isReview={isReview} />
      </div>
      <div className="flex flex-1 flex-col gap-2">
        <div className="flex min-w-0 items-start gap-2">
          <span className="line-clamp-2 min-w-0 flex-1">
            {activity.title || activity.activityId}
          </span>
          {renderContentVersion(activity)}
        </div>
        {activity.progress && (
          <>
            <ReviewBadge
              status={activity.progress?.status || ActivityStatus.PendingGrade}
            />
          </>
        )}
      </div>
    </div>
  );

  const renderDefaultLink = () => (
    <>
      <DayItemIcon
        activity={activity}
        isActive={isActive}
        isReview={isReview}
      />
      <div className="flex min-w-0 items-start gap-2">
        <span className="line-clamp-2 min-w-0 flex-1">
          {activity.title || activity.activityId}
        </span>
        {renderContentVersion(activity)}
      </div>
    </>
  );

  return isReview
    ? reviewMode === "gradebook"
      ? renderGradebookLink()
      : renderUngradedLink()
    : renderDefaultLink();
};

type CoursePlayerMenuProps = {
  activities: ActivityDto[];
  isLoading: boolean;
  progress: boolean;
  hideMenu: boolean;
  isReview?: boolean;
  reviewMode?: ReviewMode;
  unit?: LearningModuleItemDto;
  topic?: LearningModuleItemDto;
  day?: LearningModuleItemDto;
  containerRef?: RefCallback<HTMLElement>;
} & PropsWithChildren;

const CoursePlayerMenu = ({
  activities,
  isLoading,
  progress,
  hideMenu,
  isReview,
  reviewMode,
  unit,
  topic,
  day,
  containerRef,
  children,
}: CoursePlayerMenuProps) => {
  const { tab, courseId, activityId, dayId } = useParams<{
    tab: string;
    courseId: string;
    activityId: string;
    dayId: string;
  }>();

  const activeItem = activities.find((x) => x.activityId === activityId);
  const isMobile = useMediaMobileScreen();
  const completedLessonCount = activities.filter(
    (x) =>
      x.progress?.status === ActivityStatus.Completed ||
      x.progress?.status === ActivityStatus.Modified,
  ).length;

  const totalLessonCount = activities.length;
  const completedLessonPercentage =
    totalLessonCount > 0
      ? Math.round((completedLessonCount / totalLessonCount) * 100)
      : 0;

  return (
    <div
      className="flex flex-col gap-3 overflow-hidden md:flex-1"
      ref={containerRef}
      // `contain: paint` is necessary, because `overflow: hidden` does not clip
      // the children with `position: fixed`, e.g. Radix popovers.
      style={{ contain: "paint" }}
    >
      {progress && isMobile && !hideMenu && (
        <Card className="px-4 py-1.5" fullBleed>
          <div className="flex items-center gap-3">
            <LinearProgress value={completedLessonPercentage} />
            <span className="mb-1 text-lg font-bold text-gray-950">
              {completedLessonPercentage}%
            </span>
          </div>
        </Card>
      )}
      <Card className="overflow-hidden" fullBleed fullFlex={!isMobile}>
        {!isMobile && (
          <DayBreadcrumbs
            day={day}
            topic={!isReview ? topic : undefined}
            unit={!isReview ? unit : undefined}
          />
        )}
        <div className="flex min-h-0 w-full flex-1">
          {!hideMenu && (
            <div className="flex h-full min-h-0 w-full flex-col overflow-auto border-gray-50 sm:border-r md:w-64 lg:w-80">
              {isLoading ? (
                <Container>
                  <Loader className="mt-1/2" />
                </Container>
              ) : (
                <nav className="flex min-h-0 w-full flex-1 flex-col justify-between overflow-auto">
                  <ul className="sm:p-2 lg:px-4 lg:py-3">
                    {activities.map((activity) => {
                      const to = isReview
                        ? reviewMode === "my-gradebook"
                          ? `/courses-v2/${tab}/${courseId}/my-gradebook/activities/${activity.activityId}/days/${dayId}`
                          : `activities/${activity.activityId}/days/${dayId}`
                        : `activities/${activity.activityId}`;

                      return (
                        <li className="contents" key={activity.activityId}>
                          <NavLink
                            className={({ isActive }) =>
                              cn(
                                "text-md flex w-full items-center gap-3.5 rounded-xl px-4 text-left text-sm text-gray-950 !no-underline select-none max-md:h-16 md:py-3 [&.active_+.divider]:opacity-0",
                                {
                                  "active bg-blue-50": isActive,
                                  "pointer-events-none text-gray-700":
                                    !isReview &&
                                    activity?.progress?.status ===
                                      ActivityStatus.NotStarted &&
                                    !activity?.progress?.isReset,
                                },
                              )
                            }
                            to={to}
                          >
                            {({ isActive }) =>
                              renderNavLink({
                                activity,
                                isReview: isReview || false,
                                reviewMode,
                                isMobile,
                                isActive,
                              })
                            }
                          </NavLink>
                          <div className="divider w-full border-t border-gray-100 last:hidden md:border-gray-50 [&:has(+.active)]:opacity-0" />
                        </li>
                      );
                    })}
                  </ul>
                </nav>
              )}
              {isReview && (unit || topic || activeItem?.progress) && (
                <>
                  <div className="divider w-full border-t border-gray-100 last:hidden md:border-gray-50" />
                  <div className="relative flex flex-col justify-center gap-3 p-3 text-sm text-gray-700 lg:p-5">
                    {unit && (
                      <div className="flex w-full items-center justify-start gap-2">
                        <span>Unit:</span>
                        <span className="truncate font-bold text-gray-950">
                          {unit.name}
                        </span>
                      </div>
                    )}

                    {topic && (
                      <div className="flex w-full items-center justify-start gap-2">
                        <span>Topic:</span>
                        <span className="truncate font-bold text-gray-950">
                          {topic.name}
                        </span>
                      </div>
                    )}

                    {activeItem?.progress && (
                      <div className="flex w-full items-center justify-start gap-2 text-sm text-gray-700">
                        <span>Submitted:</span>
                        <span>
                          {formattedOrDefault(
                            activeItem.progress.submittedOn,
                            dateFormatter,
                            "-",
                          )}
                        </span>
                      </div>
                    )}
                  </div>
                </>
              )}

              {!isReview && progress && !isMobile && (
                <div className="mt-auto flex h-16 items-center border-t border-gray-100 px-6">
                  <div className="flex w-full flex-col gap-1 pb-2">
                    <div className="flex items-end gap-4 font-medium text-gray-800">
                      <span className="text-2xl font-bold text-gray-950">
                        {completedLessonPercentage}%
                      </span>
                      <span className="mb-1 text-base font-normal">
                        {completedLessonCount}/{totalLessonCount}
                        &nbsp;items finished
                      </span>
                    </div>
                    <LinearProgress
                      testId="day-progress-bar"
                      value={completedLessonPercentage}
                    />
                  </div>
                </div>
              )}
            </div>
          )}
          <div className="relative flex h-full w-0 flex-1 flex-col shadow-md shadow-gray-300/40">
            {isLoading && !isMobile && (
              <div className="flex h-full w-full flex-1 items-center justify-center">
                <Loader />
              </div>
            )}
            {children}
          </div>
        </div>
      </Card>
    </div>
  );
};

export default CoursePlayerMenu;
